import React, { useCallback, useEffect, useState } from 'react';

import { useCreateDocumentsMutation } from 'api/main';
import { checkAcceptedFileTypes, validateFileSize } from 'utils/helpers';
import { docTypes } from 'utils/constants';
import { useSmallScreen } from 'utils/hooks';
import { typeOfDocuments } from 'utils/formData';
import { texts } from 'utils/languages/fr';

import {
    CustomSelect,
    CustomTextField,
    FileInputButton,
    CustomFormLabel,
    FileErrorMessage,
    CheckboxLabel,
} from 'components';
import { Grid, MenuItem } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(() => ({
    select: {
        width: 150,
        marginBottom: 0,
        marginRight: 25,
    },
    replaceButton: {
        padding: '5px 8px',
        whiteSpace: 'nowrap',
    },
    withMarginTop: {
        marginTop: 5,
    },
    comment: {
        marginBottom: 0,
    },
    titleMargin: {
        marginBottom: 10,
    },
}));

const renderOption = document => (
    <MenuItem key={document.type} value={document.type}>
        {document.name}
    </MenuItem>
);

const UploadDocument = ({
    userId,
    uploadByAdmin,
    hideSelect,
    className,
    withTitle,
    updateSubscription,
    displayConfidential,
    ...rest
}) => {
    const classes = useStyles();
    const isSmallScreen = useSmallScreen('sm');

    const [fileTypeError, setFileTypeError] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(false);
    const [documentType, setDocumentType] = useState(docTypes.OTHER);
    const [confidentiality, setConfidentiality] = useState(false);
    const [comment, setComment] = useState('');
    const [commentError, setCommentError] = useState('');

    const [createDocuments, { isLoading: loading, isSuccess: createSuccess }] =
        useCreateDocumentsMutation();

    const handleDocumentSelect = ({ target: { value } }) => {
        setDocumentType(value);
    };

    const handleChangeConfidentiality = ({ target: { checked } }) => {
        setConfidentiality(checked);
    };

    const handleChangeComment = useCallback(({ target: { value } }) => {
        setComment(value);
        setCommentError('');
        setFileSizeError(false);
    }, []);

    const handleFileChange = useCallback(
        e => {
            const file = e.target.files[0];
            checkAcceptedFileTypes(
                file,
                () => {
                    if (comment === '') {
                        setCommentError(texts.VOUS_DEVEZ_REMPLIR_LE_CHAMP);
                        return;
                    }
                    setFileTypeError(false);
                    if (validateFileSize(file)) {
                        setFileSizeError(false);
                        const formData = new FormData();
                        formData.append('files[]', file);
                        formData.append('type', documentType);
                        if (confidentiality) {
                            formData.append('confidential', confidentiality);
                        }
                        if (uploadByAdmin) {
                            formData.append('belongsToUserId', userId);
                        }
                        formData.append('comment', comment);
                        createDocuments({ formData, updateUser: updateSubscription });
                    } else {
                        setFileSizeError(true);
                    }
                },
                () => {
                    setFileTypeError(true);
                    setFileSizeError(false);
                }
            );
        },
        [
            documentType,
            confidentiality,
            userId,
            createDocuments,
            comment,
            uploadByAdmin,
            updateSubscription,
        ]
    );

    useEffect(() => {
        if (createSuccess) {
            setComment('');
            setCommentError('');
        }
    }, [createSuccess]);

    return (
        <React.Fragment>
            {withTitle && (
                <CustomFormLabel labelClass={classes.titleMargin}>{texts.AUTRE}</CustomFormLabel>
            )}
            <Grid
                container
                direction={isSmallScreen ? 'column' : 'row'}
                className={className}
                wrap="nowrap"
                spacing={2}
                {...rest}
            >
                {!hideSelect && (
                    <Grid item>
                        <CustomSelect
                            inputStyle={classes.select}
                            value={documentType}
                            onChange={handleDocumentSelect}
                            defaultValue=""
                        >
                            {typeOfDocuments?.map(renderOption)}
                        </CustomSelect>
                    </Grid>
                )}

                {displayConfidential && (
                    <Grid item>
                        <CheckboxLabel
                            checked={confidentiality}
                            onChange={handleChangeConfidentiality}
                            label={texts.CONFIDENTIAL}
                        />
                    </Grid>
                )}

                <Grid
                    item
                    container
                    wrap={isSmallScreen ? 'wrap' : 'nowrap'}
                    alignItems="center"
                    spacing={2}
                >
                    <Grid item flexGrow="1">
                        <CustomTextField
                            name="comment"
                            fullWidth
                            inputStyle={classes.comment}
                            value={comment}
                            placeholder={texts.DOCUMENT_NAME}
                            inputProps={{ maxLength: 50 }}
                            onChange={handleChangeComment}
                            error={commentError}
                        />
                    </Grid>
                    {(fileTypeError || fileSizeError) && (
                        <Grid item flex="0 1 20%">
                            {fileTypeError && <FileErrorMessage variant="type" useMargin={false} />}
                            {fileSizeError && <FileErrorMessage variant="size" useMargin={false} />}
                        </Grid>
                    )}
                    <Grid item flex="0 1 15%">
                        <FileInputButton
                            noMargin
                            id="document-upload"
                            label={texts.AJOUTER_UN_DOCUMENT}
                            onFileChange={handleFileChange}
                            buttonClassName={classes.replaceButton}
                            disabled={!documentType || loading}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default UploadDocument;
